import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApolloClient } from "@apollo/client";
import { QUERY_GET_LIMITS } from "common/src/graphql/queries";
import { printAmount } from "common/src/helpers/functions";
import {
  StyledModalBody,
  StyledModal,
  StyledModalFooter,
  AlertDanger,
} from "../styledComponents/StyledModals";
import { StyledButton } from "../styledComponents/CommonStyledComponents";

export const RealityCheckDialog = ({
  showModal,
  handleProceed,
  handleCancel,
  stats,
  player,
  server,
  grossResults,
  skin,
}) => {
  const { t } = useTranslation("");
  const apolloClient = useApolloClient();

  const [allLimits, setAllLimits] = useState([]);
  const [nlDialogData, setNlDialogData] = useState({});

  useEffect(() => {
    async function getLimits() {
      const { loading, errors, data } = await apolloClient.query({
        query: QUERY_GET_LIMITS,
      });
      if (loading) return <h3>Loading ...</h3>;
      if (errors) return false;
      setAllLimits(data.getLimits.limits);
      return true;
    }

    const limitAmount = (timeRemaining) => {
      let sec_num = parseInt(timeRemaining, 10);
      let hours = Math.floor(sec_num / 3600);
      let minutes = Math.floor((sec_num - hours * 3600) / 60);
      let seconds = sec_num - hours * 3600 - minutes * 60;

      if (hours < 10) {
        hours = "0" + hours;
      }
      if (minutes < 10) {
        minutes = "0" + minutes;
      }
      if (seconds < 10) {
        seconds = "0" + seconds;
      }
      return hours + ":" + minutes;
    };

    function getDataForNlDialog() {
      let forNlDialog = {};
      allLimits.forEach((limit) => {
        if (limit.limit === "timeout" && skin !== "plc") {
          forNlDialog[limit.limit + "_remaining"] =
            limitAmount(limit.remainingAmount) +
            (skin === "plc" ? " uur" : " hour(s)");
        } else if (limit.limit === "logincount") {
          forNlDialog[limit.limit + "_remaining"] = limit.remainingAmount;
        } else {
          forNlDialog[limit.limit + "_remaining"] = printAmount(
            limit.remainingAmount,
            player.currency,
            server.currencies,
            true
          );
        }
        if (limit.limitType === "daily_timelimit") {
          forNlDialog[limit.limit + "_remaining_daily"] =
            limitAmount(limit.remainingAmount) +
            (skin === "plc" ? " uur" : " hour(s)");
          forNlDialog[limit.limit + "_period_daily"] = limit.period;
        } else if (limit.limitType === "weekly_timelimit") {
          forNlDialog[limit.limit + "_remaining_weekly"] =
            limitAmount(limit.remainingAmount) +
            (skin === "plc" ? " uur" : " hour(s)");
          forNlDialog[limit.limit + "_period_weekly"] = limit.period;
        } else if (limit.limitType === "monthly_timelimit") {
          forNlDialog[limit.limit + "_remaining_monthly"] =
            limitAmount(limit.remainingAmount) +
            (skin === "plc" ? " uur" : " hour(s)");
          forNlDialog[limit.limit + "_period_monthly"] = limit.period;
        } else {
          forNlDialog[limit.limit + "_period"] = limit.period;
        }
      });

      if (Object.keys(forNlDialog).length > 0) {
        forNlDialog["grossResults"] = printAmount(
          grossResults,
          player.currency,
          server.currencies,
          false
        );
        return forNlDialog;
      }
    }

    if (allLimits.length === 0) {
      player && getLimits();
    }

    if (allLimits) {
      setNlDialogData(getDataForNlDialog);
    }
  }, [allLimits, player, apolloClient, grossResults, server.currencies]);

  return (
    <StyledModal isOpen={showModal}>
      <StyledModalBody position>
        <h2>{t("label_reality_check_text")}</h2>
      </StyledModalBody>
      <StyledModalBody position>
        {t("label_period")}: {stats.period / 60 + " min"}
        <br />
        {t("label_bet")}:{" "}
        {printAmount(stats.bet, stats.currency, server.currencies, true)}
        <br />
        {t("label_win")}:{" "}
        {printAmount(stats.win, stats.currency, server.currencies, true)}
        <br />
      </StyledModalBody>
      {nlDialogData && Object.keys(nlDialogData).length > 0 && (
        <StyledModalBody position>
          <div>
            <span
              dangerouslySetInnerHTML={{ __html: t("label_nl_limits_deposit") }}
            ></span>
            :{" "}
            {nlDialogData.deposit_remaining ? (
              <>
                {nlDialogData.deposit_remaining} -{" "}
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("label_" + nlDialogData.deposit_period),
                  }}
                ></span>
              </>
            ) : (
              <span
                dangerouslySetInnerHTML={{
                  __html: t("label_nl_limits_not_set"),
                }}
              ></span>
            )}
          </div>
          <div>
            <span
              dangerouslySetInnerHTML={{ __html: t("label_nl_limits_loss") }}
            ></span>
            :{" "}
            {nlDialogData.loss_remaining ? (
              <>
                {nlDialogData.loss_remaining} -{" "}
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("label_" + nlDialogData.loss_period),
                  }}
                ></span>
              </>
            ) : (
              <span
                dangerouslySetInnerHTML={{
                  __html: t("label_nl_limits_not_set"),
                }}
              ></span>
            )}
          </div>
          <div>
            <span
              dangerouslySetInnerHTML={{ __html: t("label_nl_limits_bet") }}
            ></span>
            :{" "}
            {nlDialogData.bet_remaining && (
              <>
                {" "}
                {nlDialogData.bet_remaining} -{" "}
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("label_" + nlDialogData.bet_period),
                  }}
                ></span>
              </>
            )}
          </div>
          {skin === "plc" && (
            <>
              {/* 
              don't use any more
              <div>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("label_nl_limits_session"),
                  }}
                ></span>
                : {nlDialogData.timeout_remaining}
              </div> */}
              <div>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("label_nl_limits_maxbalance"),
                  }}
                ></span>
                : {nlDialogData.limit_remaining}
              </div>
              <div>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("label_nl_limits_logincount"),
                  }}
                ></span>
                :{" "}
                {nlDialogData.logincount_remaining ? (
                  <>
                    {" "}
                    {nlDialogData.logincount_remaining}x{" "}
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t("label_" + nlDialogData.logincount_period),
                      }}
                    ></span>
                  </>
                ) : (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t("label_nl_limits_not_set"),
                    }}
                  ></span>
                )}
              </div>
            </>
          )}
          <div>
            <span
              dangerouslySetInnerHTML={{
                __html: t("label_nl_limits_timelimit_daily"),
              }}
            ></span>
            :{" "}
            {nlDialogData.timelimit_remaining_daily ? (
              <>
                {" "}
                {nlDialogData.timelimit_remaining_daily}
                {skin === "plc" && " - "}
                {skin === "plc" && (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t("label_" + nlDialogData.timelimit_period_daily),
                    }}
                  ></span>
                )}
              </>
            ) : (
              <span
                dangerouslySetInnerHTML={{
                  __html: t("label_nl_limits_not_set"),
                }}
              ></span>
            )}
          </div>
          <div>
            <span
              dangerouslySetInnerHTML={{
                __html: t("label_nl_limits_timelimit_weekly"),
              }}
            ></span>
            :{" "}
            {nlDialogData.timelimit_remaining_weekly ? (
              <>
                {" "}
                {nlDialogData.timelimit_remaining_weekly}
                {skin === "plc" && " - "}
                {skin === "plc" && (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t(
                        "label_" + nlDialogData.timelimit_period_weekly
                      ),
                    }}
                  ></span>
                )}
              </>
            ) : (
              <span
                dangerouslySetInnerHTML={{
                  __html: t("label_nl_limits_not_set"),
                }}
              ></span>
            )}
          </div>
          <div>
            <span
              dangerouslySetInnerHTML={{
                __html: t("label_nl_limits_timelimit_monthly"),
              }}
            ></span>
            :{" "}
            {nlDialogData.timelimit_remaining_monthly ? (
              <>
                {" "}
                {nlDialogData.timelimit_remaining_monthly}
                {skin === "plc" && " - "}
                {skin === "plc" && (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t(
                        "label_" + nlDialogData.timelimit_period_monthly
                      ),
                    }}
                  ></span>
                )}
              </>
            ) : (
              <span
                dangerouslySetInnerHTML={{
                  __html: t("label_nl_limits_not_set"),
                }}
              ></span>
            )}
          </div>
        </StyledModalBody>
      )}
      <StyledModalFooter>
        <StyledButton
          dialog
          pub={skin === "pub"}
          onClick={() => handleCancel()}
        >
          {t("label_close")}
        </StyledButton>
        <StyledButton
          banktransfer
          pub={skin === "pub"}
          onClick={() => handleProceed()}
        >
          {t("label_continue")}
        </StyledButton>
      </StyledModalFooter>
    </StyledModal>
  );
};
